import { ILabelOwner, INode, Size, SvgVisual } from '@ardoq/yfiles';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from './consts';
import { createSvgElement } from '@ardoq/dom-utils';
import type { ArrowType } from '@ardoq/api-types';
import { ASSETS_AND_TEST_CONTAINER_ID, MARKER_PROPS } from '@ardoq/icons';
import {
  ComponentGraphEdge,
  ComponentGraphGroup,
  ComponentGraphNode,
  GraphItem,
  impureGetUrlFieldValues,
  URLFieldValue,
} from '@ardoq/graph';
import { dataModelId } from 'tabview/graphComponent/graphComponentUtil';
import type { GraphItem as OldGraphItem } from 'graph/GraphItem';

const createTemplateElement = (href: string, className: string) =>
  createSvgElement('use', { href, class: className });

const templateSizeCache = new Map<string, Size>();

let testDocument: SVGElement | null = null;
const getSvgTestDocument = () => {
  if (!testDocument) {
    const svg = createSvgElement('svg');
    const container = document.getElementById(ASSETS_AND_TEST_CONTAINER_ID);
    container?.appendChild(svg);
    testDocument = svg;
  }
  return testDocument;
};

export const determineStyleSize = (shapeTemplateHref: string) => {
  if (templateSizeCache.has(shapeTemplateHref)) {
    return templateSizeCache.get(shapeTemplateHref)!;
  }
  const element = createTemplateElement(shapeTemplateHref, '');
  getSvgTestDocument().appendChild(element);
  const bbox = element.getBBox();
  const size = new Size(bbox.width, bbox.height);
  element.remove();
  templateSizeCache.set(shapeTemplateHref, size);
  return size;
};
export const determineImgStyleSize = (imageHref: string) => {
  const img = document.createElement('img');
  img.src = imageHref;
  // this approach to measuring images is fundamentally broken.
  // if the browser has not already cached this image url, naturalWidth/naturalHeight will be zero until the image is loaded (i.e. after the element' onload event has been raised).
  // providing a zero-size layout to a graph element will throw an exception in yFiles and hang the app, as the graph layout will never complete.
  // so the real solution to this would be to measure all images before the graph layout even begins.
  // but it would be complex to implement such an async solution, and this is a critical regression since Nov. 4.
  // in lieu of the real solution, I am falling back to IMAGE_WIDTH and IMAGE_HEIGHT, which will force a square layout in this event. and that was the behavior for many years before it was changed on Nov. 4 2021.
  const size = new Size(
    img.naturalWidth || IMAGE_WIDTH,
    img.naturalHeight || IMAGE_HEIGHT
  );
  return size;
};

export const disablePointerEvents = (visual: SvgVisual): void =>
  visual && visual.svgElement.setAttribute('pointer-events', 'none');

type EdgeLineEnds = {
  getLineBeginning: () => ArrowType;
  getLineEnding: () => ArrowType;
};

export const hasLineEnds = (item: object): item is EdgeLineEnds =>
  'getLineBeginning' in item && 'getLineEnding' in item;

/**
 * The widest marker at either end of the edge. Reads the line ends from the
 * edge tag, the same source the edge styles draw the markers from.
 */
export const getEdgeLabelIndent = (edge: EdgeLineEnds) =>
  Math.max(
    MARKER_PROPS[edge.getLineBeginning()].markerWidth,
    MARKER_PROPS[edge.getLineEnding()].markerWidth
  );

const hasUrlFieldValuesInGraphItem = (
  graphItem: object
): graphItem is ComponentGraphEdge | ComponentGraphGroup | ComponentGraphNode =>
  graphItem && 'getUrlFieldValues' in graphItem;

export const getLabelOwnerUrlFieldValues = (
  owner: ILabelOwner
): URLFieldValue[] | null => {
  const { tag } = owner;
  const graphItem = tag as GraphItem | OldGraphItem;

  const urlFieldValues = hasUrlFieldValuesInGraphItem(graphItem)
    ? graphItem.getUrlFieldValues()
    : impureGetUrlFieldValues(owner instanceof INode, dataModelId(graphItem));

  return urlFieldValues && urlFieldValues.length > 0 ? urlFieldValues : null;
};
